import { render } from 'preact';
import { useEffect, useState } from "preact/hooks";
import ProductModal from "react-components/ProductModal";

// Helper to get URL parameter by name
function getParameterByName(name, url) {
    if (!url) url = window.location.href;
    name = name.replace(/[\[\]]/g, '[$&]');
    const regex = new RegExp(`[?&]${name}(=([^&#]*)|&|#|$)`);
    const results = regex.exec(url);
    if (!results) return null;
    if (!results[2]) return '';
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
}

export async function Root(element, data) {
    function GlobalModalManager() {
        const [selectedProduct, setSelectedProduct] = useState(null);
        const [hasProcessedInitialLoad, setHasProcessedInitialLoad] = useState(false);
        const [hasShownModal, setHasShownModal] = useState(false);

        // NEW: Set global flag when modal is shown
        useEffect(() => {
            if (selectedProduct && typeof window !== 'undefined') {
                window.cirkulGlobalModalShown = true;
            } else if (!selectedProduct && typeof window !== 'undefined') {
                window.cirkulGlobalModalShown = false;
            }
        }, [selectedProduct]);

        // Check for URL parameter on mount and periodically
        useEffect(() => {
            if (hasProcessedInitialLoad) return;
            
            const showId = getParameterByName('show');
            if (!showId) {
                setHasProcessedInitialLoad(true);
                return;
            }

            // Initialize global registry if it doesn't exist
            if (typeof window !== 'undefined') {
                if (!window.cirkulGlobalProducts) {
                    window.cirkulGlobalProducts = [];
                }
                if (!window.cirkulGlobalProductCallbacks) {
                    window.cirkulGlobalProductCallbacks = [];
                }
                
                // NEW: Initialize global close function
                window.cirkulCloseAllModals = () => {
                    setSelectedProduct(null);
                    setHasShownModal(false);
                };
            }

            // Function to check all available product sources
            function checkForProduct() {
                // Check if we've already shown the modal
                if (hasShownModal) {
                    return false;
                }
                
                let foundProduct = null;
                let targetVariant = null;
                
                // First check the global product registry
                if (window.cirkulGlobalProducts) {
                    foundProduct = window.cirkulGlobalProducts.find(item => {
                        // Check main product ID
                        if (String(item.id) === String(showId)) return true;
                        
                        // Check variant IDs and store the target variant
                        if (Array.isArray(item.variants)) {
                            targetVariant = item.variants.find(v => String(v.id) === String(showId));
                            return !!targetVariant;
                        }
                        return false;
                    });
                    
                    if (foundProduct) {
                        // If we found a specific variant, set it as the initial variant
                        if (targetVariant) {
                            foundProduct = {
                                ...foundProduct,
                                targetVariant: targetVariant
                            };
                        }
                        setSelectedProduct(foundProduct);
                        setHasProcessedInitialLoad(true);
                        setHasShownModal(true);
                        return true;
                    }
                }

                // If not found in global registry, check liquid JSON data directly
                const reactSnippets = document.querySelectorAll('[data-react-snippet="Grid_Filters"] script[data-react-snippet-data]');
                for (const script of reactSnippets) {
                    try {
                        const jsonData = JSON.parse(script.textContent);
                        if (jsonData.products && Array.isArray(jsonData.products)) {
                            foundProduct = jsonData.products.find(item => {
                                // Check main product ID
                                if (String(item.id) === String(showId)) return true;
                                
                                // Check variant IDs and store the target variant
                                if (Array.isArray(item.variants)) {
                                    targetVariant = item.variants.find(v => String(v.id) === String(showId));
                                    return !!targetVariant;
                                }
                                return false;
                            });
                            
                            if (foundProduct) {
                                // If we found a specific variant, set it as the initial variant
                                if (targetVariant) {
                                    foundProduct = {
                                        ...foundProduct,
                                        targetVariant: targetVariant
                                    };
                                }
                                setSelectedProduct(foundProduct);
                                setHasProcessedInitialLoad(true);
                                setHasShownModal(true);
                                return true;
                            }
                        }
                    } catch (e) {
                        // Skip malformed JSON
                        continue;
                    }
                }
                
                return false;
            }

            // Check immediately
            if (checkForProduct()) return;

            // If not found, check periodically
            const interval = setInterval(() => {
                if (checkForProduct()) {
                    clearInterval(interval);
                }
            }, 200);

            // Stop checking after 10 seconds (50 attempts)
            const timeout = setTimeout(() => {
                clearInterval(interval);
                setHasProcessedInitialLoad(true);
            }, 10000);

            // Create a wrapper function that captures the current hasShownModal state
            const checkForProductWrapper = () => {
                // Re-check hasShownModal state at call time
                if (hasShownModal) {
                    return false;
                }
                return checkForProduct();
            };

            // Register callback to be notified when new products are added
            window.cirkulGlobalProductCallbacks.push(checkForProductWrapper);

            return () => {
                clearInterval(interval);
                clearTimeout(timeout);
                
                // Remove our callback
                if (window.cirkulGlobalProductCallbacks) {
                    const index = window.cirkulGlobalProductCallbacks.indexOf(checkForProductWrapper);
                    if (index > -1) {
                        window.cirkulGlobalProductCallbacks.splice(index, 1);
                    }
                }
            };
        }, [hasShownModal]);

        if (!selectedProduct) return null;

        return (
            <ProductModal
                variant={{
                    ...selectedProduct,
                    sipData: selectedProduct?.sipData
                }}
                product={{
                    ...selectedProduct,
                    description: selectedProduct.description || "No description available.",
                    featuredImage: selectedProduct.featured_image || selectedProduct.featuredImage || "",
                    sipData: selectedProduct?.sipData,
                    id: selectedProduct?.id,
                    nutritionImage: selectedProduct?.nutritionImage,
                    flavorProfiles: selectedProduct?.flavorProfiles,
                    variants: selectedProduct.variants,
                    targetVariant: selectedProduct.targetVariant // Pass target variant to modal
                }}
                variants={selectedProduct.variants}
                onClose={() => {
                    setSelectedProduct(null);
                    setHasShownModal(false);
                    // NEW: Clear global flag when modal closes
                    if (typeof window !== 'undefined') {
                        window.cirkulGlobalModalShown = false;
                    }
                }}
                sipBreadcrumbs={selectedProduct?.sipData?.breadcrumbs || null}
                flavorProfileIcons={selectedProduct?.sipData?.flavor_profile_icons?.value || []}
                flavorProfileStrings={selectedProduct?.sipData?.flavor_profile_strings?.value || []}
                bundleCounts={selectedProduct.bundleCounts || {}}
                bundleComponents={selectedProduct.bundleComponents || null}
                modalGalleryImages={selectedProduct?.modalGalleryImages || selectedProduct?.modalImages}
                onFlavorSelect={(newProduct) => {
                    setSelectedProduct(newProduct);
                }}
            />
        );
    }

    render(<GlobalModalManager />, element);
}
